import { useContext, useEffect, useState } from 'react'
import { jt, t } from 'ttag'
import * as recharts from 'recharts'
import styled from 'styled-components'
import { useMutation } from 'react-query'
import { useDispatch } from 'react-redux'

import { Globals } from '@h2/globals'
import { Page } from '@h2/common/components/page'
import { Text } from '@h2/components/typography/text'
import {
  DollarSmallIcon,
  PrintIcon,
  SaveIcon,
  ShareIcon,
  TimesIcon
} from '@h2/components/icons'
import { Flex } from '@h2/components/layout/flex'
import { Heading } from '@h2/components/typography/heading'
import { BLACK, PB80T, SLR } from '@h2/components/colors'
import { Link } from '@h2/common/components/link'
import { Button } from '@h2/components/buttons/button'
import { api } from '@h2/api'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'

import { VerticalSummary } from './components/verticalSummary'
import { budgetRounds, fmtCurrency } from './common/helpers'

const ContentCol = styled.div`
  width: 50%;
`
const EarningsBox = styled.div`
  display: flex;
  padding: 15px;
  margin-top: 15px;
  border: 1px solid ${PB80T};
  font-size: 31px;
  color: ${BLACK};
`
const AlertBox = styled.div`
  background-color: #fcf8e3;
  border-color: ${PB80T};
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid transparent;
  border-radius: 4px;
`
const TotalAmount = styled.span`
  color: ${({ isOverspent }) => (isOverspent ? SLR : BLACK)};
  font-weight: bold;
`
const BudgetSummary = props => {
  const { budgetTitle, userProfile, isSubscriber, plotColors } = useContext(Globals)
  const { budget, closeSaveConfirm } = props
  const [showSavedConfirm, setShowSavedConfirm] = useState(false)
  const {
    monthlySpending = 0,
    annualTotal = 0,
    taxes = 0,
    occupation,
    reverse,
    responses,
    questions,
    startSalary,
    monthlyTotal = 0
  } = budget
  const options = {}
  const reduxDispatch = useDispatch()
  const rounds = budgetRounds()
  const { Cell, Pie, PieChart, ResponsiveContainer } = recharts
  const showSave = userProfile?.isRoot || userProfile?.hasFileAccess || isSubscriber

  questions.forEach(question =>
    question.options.forEach(opt => (options[opt.id] = opt.title))
  )

  const { mutate: markSummarized } = useMutation(
    () => {
      return api.fetch(`/budget/${budget.id}/markSummarized`, {
        method: 'POST',
        body: JSON.stringify({})
      })
    },
    {
      onError: () => {
        reduxDispatch(showUserFeedback(t`Could not mark budget as summarized`, 'error'))
      }
    }
  )

  useEffect(() => {
    const totalBudgetCost = responses.reduce(
      (total, currResp) => total + currResp.total,
      0
    )

    if (!budget.dateCompleted && totalBudgetCost > 0) {
      markSummarized()
    }
  }, [markSummarized, budget.dateCompleted, responses])

  const { mutate: onSave, isLoading: isSaving } = useMutation(
    () => {
      return api.fetch(`/budget/${budget.id}/save`, {
        method: 'POST',
        body: JSON.stringify({})
      })
    },
    {
      onSuccess: () => {
        setShowSavedConfirm(true)
      },
      onError: () => {
        reduxDispatch(showUserFeedback(t`Could not save budget`, 'error'))
      }
    }
  )
  const spendData = [
    ...rounds.map(round => ({
      name: round.name(),
      value: (responses.find(round2 => round2.round === round.roundId) || {}).total
    })),
    { name: t`Taxes`, value: taxes.total / 12 }
  ]
    .filter(round => round.value > 0)
    .sort(() => 0.5 - Math.random())

  if (reverse && monthlyTotal > 0) {
    spendData.push({ name: t`Unspent`, value: monthlyTotal })
  }

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    percent,
    name,
    fontSize,
    index
  }) => {
    const RADIAN = Math.PI / 180
    const sin = Math.sin(RADIAN * midAngle)
    const cos = Math.cos(RADIAN * midAngle)
    const startX = cx + outerRadius * cos
    const startY = cy + outerRadius * -sin
    const middleY = cy + (outerRadius + 20 * Math.abs(sin)) * -sin
    let endX = startX + (cos >= 0 ? 1 : -1) * 40
    let textAnchor = cos >= 0 ? 'start' : 'end'
    const mirrorNeeded =
      midAngle > -270 && midAngle < -210 && percent < 0.04 && index % 2 === 1

    if (mirrorNeeded) {
      endX = startX + outerRadius * -cos * 2 + 50
      textAnchor = 'start'
    }

    return (
      <g>
        <path
          d={`M${startX},${startY}L${startX},${middleY}L${endX},${middleY}`}
          fill="none"
          stroke="#000"
          strokeWidth={1}
        />
        <text
          fill="black"
          x={endX + (cos >= 0 || mirrorNeeded ? 1 : -1) * 12}
          y={middleY + fontSize / 2}
          textAnchor={textAnchor}
          fontSize={fontSize}>
          {`${name || ''} ${Math.ceil(percent * 100)}%`}
        </text>
      </g>
    )
  }
  const monthlySalary = (
    <span className="bold" key="monthSal">
      {fmtCurrency(startSalary / 12)}
    </span>
  )
  const monthlySpendingString = (
    <span className="bold" key="monthlySpend">
      {fmtCurrency(monthlySpending + taxes.total / 12)}
    </span>
  )
  const monthlyRemainingString = (
    <TotalAmount key="remaining" isOverspent={monthlyTotal < 0}>
      {fmtCurrency(monthlyTotal)}
    </TotalAmount>
  )
  const annualTotalString = <span className="bold">{fmtCurrency(annualTotal)}</span>
  const budgetOccString = occupation ? t`for ${occupation.title}` : ''

  return (
    <Page>
      <Page.Title>{t`${budgetTitle} :: Summary`}</Page.Title>
      <Page.Layout>
        <Page.Content>
          {showSavedConfirm && (
            <AlertBox>
              <Flex justifyContent="right">
                <Button
                  icon={TimesIcon}
                  display="link"
                  onClick={closeSaveConfirm}
                  style={{ background: 'none' }}
                />
              </Flex>
              <Heading level="3">
                {(() => {
                  const sharedFilesLink = (
                    <Link
                      key="sharedFileLink"
                      href="/careerhub/files"
                      size="large"
                      style={{ fontSize: '24px', textDecoration: 'underline' }}>
                      {t`saved files`}
                    </Link>
                  )

                  if (showSavedConfirm.forUser) {
                    return jt`This report has been saved to the file listing of ${showSavedConfirm.forUser}. You can access it in their ${sharedFilesLink}.`
                  }

                  return jt`This report has been saved to your Career Hub. You can access it in your ${sharedFilesLink}.`
                })()}
              </Heading>
            </AlertBox>
          )}
          <Flex justifyContent="right" gap="5px" margin="0 0 15px 0">
            <Link
              href="/pdfs/budget.pdf"
              variant="outlined"
              display="monochrome"
              align="right"
              ssr={true}
              icon={PrintIcon}
              size="large">
              {t`Print`}
            </Link>
            {showSave && (
              <Button
                variant="outlined"
                display="monochrome"
                align="right"
                isLoading={isSaving}
                onClick={onSave}
                icon={SaveIcon}
                size="large">
                {t`Save to Career Hub`}{' '}
              </Button>
            )}
          </Flex>
          <div>
            <Text size="large">
              {reverse
                ? jt`You created a budget ${budgetOccString} where you take home ${monthlySalary} a month,
                        and budgeted ${monthlySpendingString} towards expenses. This will leave you ${monthlyRemainingString} a month.`
                : jt`Based on your responses, you will need to have a job where you take home ${monthlySpendingString} a month,
                         or at least ${annualTotalString} a year when you include taxes.`}
            </Text>

            <Heading level="2" weight="light">
              {reverse ? (
                <span>{jt`Monthly Earnings: ${monthlySalary} - Spending: ${monthlySpendingString} = Remaining: ${monthlyRemainingString}`}</span>
              ) : (
                <span>
                  {t`Monthly Spending:`} {monthlySpendingString}
                </span>
              )}
            </Heading>

            <Flex alignItems="flex-start" gap="25px">
              <ContentCol>
                <Flex alignItems="flex-start">
                  <ShareIcon
                    size="large"
                    style={{ transform: 'scaleY(-1)', width: '75px' }}
                  />
                  <VerticalSummary
                    budgetId={budget.id}
                    reverse={reverse}
                    monthlyTotal={monthlyTotal}
                    options={options}
                    rounds={rounds}
                    responses={responses}
                    taxes={taxes}
                    fmtCurrency={fmtCurrency}
                  />
                </Flex>
              </ContentCol>
              <ContentCol>
                <Text size="large">{t`Below you can see how your budget choices relate to your overall budget.`}</Text>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart
                      width={100}
                      height={100}
                      isAnimationActive={false}
                      aria-label={t`Summary of budget spend`}>
                      <Pie
                        data={spendData}
                        dataKey="value"
                        outerRadius={90}
                        fontSize={11}
                        startAngle={0}
                        labelLine={false}
                        paddingAngle={6}
                        label={renderCustomizedLabel}>
                        {spendData.map((data, index) => (
                          <Cell
                            key={`cell${index}`}
                            fill={plotColors[index % plotColors.length]}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <br className="clear" />
                <div className="filter note-color" style={{ marginTop: '30px' }}>
                  <Heading level="3">{t`Need to make changes or add items?`}</Heading>
                  <Text size="large">{t`You can select any of the sections in the summary to change selections.`}</Text>
                  <Text size="large">{t`Didn't see an option you expected? You can add it to your budget link by using the button below.`}</Text>
                  <Flex justifyContent="center">
                    <Link
                      variant="button"
                      display="blue"
                      href={`/budget/${budget.id}/question/9`}>
                      {t`Add Other Items`}
                    </Link>
                  </Flex>
                </div>
              </ContentCol>
            </Flex>

            <EarningsBox>
              <DollarSmallIcon size="large" />
              <div>
                {t`Minimum Annual Salary:`} <span>{fmtCurrency(annualTotal)}</span>
              </div>
            </EarningsBox>
            {(!reverse || (reverse && monthlyTotal < 0)) && (
              <div>
                {reverse && monthlyTotal < 0 && (
                  <div
                    style={{ margin: '15px 0' }}
                    className="alert alert-warning text-center">
                    <Heading level="4">
                      {t`Your budget exceeds the amount you expect to make.`}
                    </Heading>
                    <Text>{t`To explore alternate occupations which might fit your budget, click the button below.`}</Text>
                  </div>
                )}
                <div className="nav">
                  {!reverse && (
                    <Text>{t`To find out which jobs might support your lifestyle, click the button below.`}</Text>
                  )}
                  <Link
                    href={`/budget/${budget.id}/results`}
                    variant="button"
                    id="summarylink">
                    {t`View Occupations`}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </Page.Content>
      </Page.Layout>
    </Page>
  )
}

export { BudgetSummary }
